import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { format, parseISO } from "date-fns";
import { mdyDow } from "@/lib/dateFmt";
import { ClipboardCheck, Filter } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  kin_care:  { label: "Kin Care",  bg: "bg-emerald-100", text: "text-emerald-800" },
  fmla:      { label: "FMLA",      bg: "bg-indigo-100",  text: "text-indigo-800"  },
  sick:      { label: "Sick",      bg: "bg-amber-100",   text: "text-amber-800"   },
  unexcused: { label: "Unexcused", bg: "bg-red-100",     text: "text-red-800"     },
  other:     { label: "Other",     bg: "bg-slate-100",   text: "text-slate-700"   },
};

export default function AttendanceTrackerPage({ facilityId, facilityLabel }) {
  const today = new Date();
  const oneYearAgo = new Date(today); oneYearAgo.setFullYear(today.getFullYear() - 1);
  const [start, setStart] = useState(format(oneYearAgo, "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(today, "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);

  const load = async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance-tracker?facility_id=${facilityId}&start_date=${start}&end_date=${end}`);
      setData(data); setTypeFilter(null);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facilityId]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (!typeFilter) return data.rows;
    return data.rows.map((r) => ({
      ...r,
      events: r.events.filter((ev) => ev.absent_type === typeFilter),
      total: r.events.filter((ev) => ev.absent_type === typeFilter).length,
    })).filter((r) => r.total > 0);
  }, [data, typeFilter]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="attendance-tracker-page">
      <div className="p-5 border-b border-slate-200 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 text-lg font-display font-semibold"><ClipboardCheck size={18} className="text-blue-600" /> Attendance Tracker</div>
          <div className="text-sm text-slate-500 mt-0.5">{facilityLabel} · Absent history per employee</div>
        </div>
        <label className="text-[11px] font-medium text-slate-700">
          From
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tracker-start" className="mt-1 w-[150px]" />
        </label>
        <label className="text-[11px] font-medium text-slate-700">
          To
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tracker-end" className="mt-1 w-[150px]" />
        </label>
        <Button onClick={load} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="tracker-reload">
          {loading ? "Loading…" : "Reload"}
        </Button>
      </div>

      {data && (
        <div className="p-4 border-b border-slate-200 flex items-center gap-1.5 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1"><Filter size={10} /> Filter:</div>
          <FilterChip active={!typeFilter} count={data.totals.total} label="All" onClick={() => setTypeFilter(null)} data-testid="tracker-filter-all" />
          {Object.entries(TYPE_META).map(([code, meta]) => (
            <FilterChip key={code}
              active={typeFilter === code}
              count={data.totals[code] || 0}
              label={meta.label}
              onClick={() => setTypeFilter(typeFilter === code ? null : code)}
              testid={`tracker-filter-${code}`}
            />
          ))}
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {loading && <div className="py-8 text-center text-slate-400">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-500">No absent marks found in this window.</div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="p-4" data-testid={`tracker-row-${r.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src={r.avatar} alt={r.name} />
                <AvatarFallback className="text-[10px]">{(r.name || "??").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{r.name}</div>
                <div className="text-[11px] text-slate-500">{r.role_label}</div>
              </div>
              <div className="text-[11px] font-bold text-slate-700 tabular-nums bg-slate-100 rounded-full px-2 py-0.5">
                {r.total} {r.total === 1 ? "mark" : "marks"}
              </div>
            </div>
            {r.events.length > 0 && (
              <div className="ml-11 space-y-1">
                {r.events.map((ev, i) => {
                  const t = TYPE_META[ev.absent_type] || TYPE_META.other;
                  return (
                    <div key={i} className="flex items-start gap-2 text-[12px] leading-snug">
                      <span className="text-slate-400 tabular-nums w-28 shrink-0">{mdyDow(ev.date)}</span>
                      <span className={`shrink-0 inline-flex items-center rounded-md px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-widest ${t.bg} ${t.text}`}>{t.label}</span>
                      {ev.original_shift_code && (
                        <span className="text-[10px] text-slate-400 tabular-nums shrink-0">(was {ev.original_shift_code})</span>
                      )}
                      <span className="text-slate-600 truncate">{ev.note || <em className="text-slate-400">no note</em>}</span>
                      {ev.marked_by_name && <span className="text-[10px] text-slate-400 shrink-0 ml-auto">— {ev.marked_by_name}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, count, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
        active ? "border-blue-500 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
      }`}>
      {label}
      <span className={`rounded-full text-[9px] font-bold px-1.5 ${active ? "bg-white/20" : "bg-slate-100 text-slate-700"} tabular-nums`}>{count}</span>
    </button>
  );
}
